import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Upload, Loader2, FileArchive, FileSpreadsheet, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown, BellRing, FileDown, Scissors } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import SepaSplitPanel from './sepa/SepaSplitPanel';

const eur = (v) => Number(v || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const CAT = {
  prenotazioni: { label: 'Prenotazioni', cls: 'bg-blue-100 text-blue-800' },
  reclamo_rimborso: { label: 'Reclamo/Rimborso', cls: 'bg-purple-100 text-purple-800' },
  altro: { label: 'Altri costi', cls: 'bg-slate-200 text-slate-700' },
  nota_credito: { label: 'Nota di credito', cls: 'bg-teal-100 text-teal-800' },
};
const EXCEL = {
  ok: { label: '✓ quadra', cls: 'text-emerald-700' },
  amount_diff: { label: 'importo diverso', cls: 'text-rose-600 font-bold' },
  structure_mismatch: { label: 'struttura diversa', cls: 'text-rose-600 font-bold' },
  missing_in_excel: { label: 'manca in Excel', cls: 'text-amber-600 font-bold' },
};
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const ACC = {
  cat: i => CAT[i.category]?.label || '',
  invoice: i => i.invoice_number || '',
  prop: i => norm(`${i.property_name || ''} ${i.hotel_id || ''}`),
  date: i => i.date || '',
  period: i => i.period_from || '',
  total: i => i.total || 0,
  vat: i => i.vat_amount || 0,
  excel: i => i.excel_status || '',
  closure: i => i.closure?.closed_until || (i.deferred ? '0' : 'zz'),
};

export default function BookingInvoicesTab() {
  const [files, setFiles] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [historical, setHistorical] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [sort, setSort] = useState({ col: 'total', dir: -1 });
  const [reminders, setReminders] = useState(null);
  const [history, setHistory] = useState([]);
  const [vatReg, setVatReg] = useState(null);
  const [vatOpen, setVatOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [highlightInv, setHighlightInv] = useState(null);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [splitCfg, setSplitCfg] = useState(null);
  const [fragAmount, setFragAmount] = useState('');
  const [fragLoading, setFragLoading] = useState(false);

  const loadReminders = useCallback(async () => {
    try { setReminders((await api.get('/booking-invoices/reminders')).data); } catch { /* noop */ }
  }, []);
  const loadHistory = useCallback(async () => {
    try { setHistory((await api.get('/booking-invoices/reports')).data.reports || []); } catch { /* noop */ }
  }, []);
  const loadVat = useCallback(async () => {
    try { setVatReg((await api.get('/booking-invoices/vat-recovery')).data); } catch { /* noop */ }
  }, []);
  const loadAlerts = useCallback(async () => {
    try { setAlerts((await api.get('/booking-invoices/alerts')).data.alerts || []); } catch { /* noop */ }
  }, []);
  const loadSplitCfg = useCallback(async () => {
    try { setSplitCfg((await api.get('/admin/sepa-split/config')).data); } catch { /* noop */ }
  }, []);
  useEffect(() => {
    loadReminders(); loadHistory(); loadVat(); loadAlerts(); loadSplitCfg();
    const h = new URLSearchParams(window.location.search).get('highlight');
    if (h) setHighlightInv(h);
  }, [loadReminders, loadHistory, loadVat, loadAlerts, loadSplitCfg]);

  const analyze = async () => {
    if (!files.length) return toast.error('Carica lo ZIP (o i PDF) delle fatture');
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      if (excelFile) fd.append('excel', excelFile);
      fd.append('historical', historical ? 'true' : 'false');
      const r = await api.post('/booking-invoices/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      setReport(r.data);
      loadHistory(); loadVat();
      toast.success(`Analizzate ${r.data.summary.invoice_count} fatture${r.data.summary.vat_error_count ? ` — ${r.data.summary.vat_error_count} con IVA da contestare!` : ''}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore analisi'); }
    finally { setLoading(false); }
  };

  const openReport = async (rid) => {
    try { setReport((await api.get(`/booking-invoices/reports/${rid}`)).data); }
    catch { toast.error('Errore caricamento report'); }
  };

  const defer = async (inv) => {
    const suggested = inv.closure?.suggested_pay_by || '';
    const payBy = window.prompt(`Rinvia il pagamento della fattura ${inv.invoice_number} (${inv.property_name || inv.hotel_id}).\nEntro quando pagarla? (YYYY-MM-DD)${inv.closure ? `\nChiusa fino al ${inv.closure.closed_until} → consigliato un mese prima:` : ''}`, suggested);
    if (!payBy) return;
    try {
      await api.post('/booking-invoices/defer', {
        invoice_number: inv.invoice_number, hotel_id: inv.hotel_id, amount: inv.total,
        property_code: inv.property_code, property_name: inv.property_name,
        pay_by: payBy, closed_until: inv.closure?.closed_until || null,
        note: inv.closure ? `Chiusa fino al ${inv.closure.closed_until}` : null,
      });
      toast.success('Pagamento rinviato: la fattura sarà ESCLUSA dagli XML commissioni fino al promemoria');
      setReport(r => ({ ...r, invoices: r.invoices.map(i => i.invoice_number === inv.invoice_number ? { ...i, deferred: { pay_by: payBy } } : i) }));
      loadReminders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const deferAction = async (d, action) => {
    try {
      await api.post(`/booking-invoices/defer/${d.id}/${action}`);
      toast.success(action === 'paid' ? 'Segnata come pagata' : 'Rinvio annullato: tornerà negli XML');
      loadReminders(); loadAlerts();
      if (report) setReport(r => ({ ...r, invoices: r.invoices.map(i => i.invoice_number === d.invoice_number ? { ...i, deferred: null } : i) }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const alertAction = async (a, action) => {
    try {
      if (action === 'pay' && a.deferral_id) {
        await api.post(`/booking-invoices/defer/${a.deferral_id}/cancel`).catch(() => {});
        toast.success(`Fattura ${a.invoice_number} rimessa in pagamento: tornerà negli XML`);
      }
      await api.post(`/booking-invoices/alerts/${a.id}/ack`);
      loadAlerts(); loadReminders();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const vatAction = async (h, action) => {
    try {
      await api.post(`/booking-invoices/vat-recovery/${h.id}/${action}`);
      toast.success(action === 'unblock' ? 'Sbloccata: verrà pagata (IVA da recuperare dopo)' : 'Segnata come recuperata ✓');
      loadVat();
      setReport(r => r ? { ...r, invoices: r.invoices.map(i => i.invoice_number === h.invoice_number ? { ...i, vat_hold_status: action === 'unblock' ? 'to_recover' : 'recovered' } : i) } : r);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const s = report?.summary;
  const all = report?.invoices || [];
  const exclPaid = all.filter(i => i.category !== 'nota_credito' && i.paid_in_xml);
  const payables = all.filter(i => i.invoice_number && (i.total || 0) > 0 && !i.paid_in_xml && !i.deferred && i.vat_hold_status !== 'blocked' && i.category !== 'nota_credito');
  const payableTotal = payables.reduce((a, i) => a + (i.total || 0), 0);
  const exclVat = all.filter(i => !i.paid_in_xml && i.vat_hold_status === 'blocked');
  const exclDeferred = all.filter(i => !i.paid_in_xml && i.vat_hold_status !== 'blocked' && i.deferred);
  const exclCredit = all.filter(i => !i.paid_in_xml && !i.deferred && i.vat_hold_status !== 'blocked' && i.category === 'nota_credito');
  const sumOf = (lst) => lst.reduce((a, i) => a + (i.total || 0), 0);
  const needsSplit = !!splitCfg?.effective_limit_eur && payableTotal > splitCfg.effective_limit_eur;
  const blockingBatches = Object.values(exclPaid.reduce((m, i) => {
    const p = i.paid_in_xml; if (!p?.batch_id) return m;
    if (!m[p.batch_id]) m[p.batch_id] = { ...p, count: 0, sum: 0 };
    m[p.batch_id].count += 1; m[p.batch_id].sum += (i.total || 0);
    return m;
  }, {}));

  const deleteBatch = async (b) => {
    if (!window.confirm(`Elimino la distinta ${b.filename || b.batch_id} (${b.count} fatture · ${eur(b.sum)})?\n\nLe fatture torneranno "da pagare" e potrai rigenerare l'XML (con lo split se sopra soglia).\n\nATTENZIONE: fallo solo se NON hai già caricato quel file in banca!`)) return;
    try {
      await api.delete(`/admin/payment-batches/${b.batch_id}`);
      toast.success('Distinta eliminata: fatture sbloccate per la rigenerazione');
      openReport(report.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore eliminazione'); }
  };

  const generateXml = async () => {
    if (!report?.id) return;
    if (!window.confirm(`Genero l'XML Banca di Asti con ${payables.length} fatture da pagare per ${eur(payableTotal)}?\n\nEscluse automaticamente: ${exclPaid.length} già in XML precedenti, ${exclVat.length} bloccate per IVA, ${exclDeferred.length} rinviate, ${exclCredit.length} note di credito.`)) return;
    setXmlLoading(true);
    try {
      const r = await api.post(`/booking-invoices/reports/${report.id}/xml`);
      const { xml_b64, filename, stats } = r.data;
      const blob = new Blob([Uint8Array.from(atob(xml_b64), c => c.charCodeAt(0))], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`XML generato: ${stats.included.count} bonifici · ${eur(stats.included.total)} — escluse ${stats.excluded_paid.count} già pagate, ${stats.excluded_deferred.count} rinviate, ${stats.excluded_vat.count} con IVA. Archiviato nei batch (conta nei massimali giornalieri).`, { duration: 8000 });
      openReport(report.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore generazione XML'); }
    finally { setXmlLoading(false); }
  };

  const fragmentXml = async () => {
    if (!report?.id) return;
    const amt = parseFloat(String(fragAmount).replace(/\./g, '').replace(',', '.')) || parseFloat(fragAmount);
    if (!amt || amt <= 0) { toast.error('Inserisci la disponibilità residua (es. 45000)'); return; }
    if (amt >= payableTotal) { toast.error(`L'importo deve essere inferiore al totale da pagare (${eur(payableTotal)})`); return; }
    if (!window.confirm(`Frammento in 2 XML:\n\n1) fatture fino a ${eur(amt)} (data oggi)\n2) differenza ~${eur(payableTotal - amt)} (primo giorno lavorativo successivo)\n\nProcedo?`)) return;
    setFragLoading(true);
    try {
      const r = await api.post(`/booking-invoices/reports/${report.id}/fragment-xml`, { amount: amt });
      const { zip_b64, filename, lots } = r.data;
      const blob = new Blob([Uint8Array.from(atob(zip_b64), c => c.charCodeAt(0))], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`ZIP con 2 XML: ${lots.map(l => `${l.count} fatture · ${eur(l.total)} (${l.execution_date})`).join('  +  ')}`, { duration: 10000 });
      setFragAmount('');
      openReport(report.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore frammentazione'); }
    finally { setFragLoading(false); }
  };

  const invoices = (report?.invoices || [])
    .filter(i => {
      if (catFilter === 'chiusure') return i.closure || i.deferred;
      if (i.deferred) return false;
      if (catFilter === 'da_pagare') return !!i.invoice_number && (i.total || 0) > 0 && !i.paid_in_xml && i.vat_hold_status !== 'blocked' && i.category !== 'nota_credito';
      if (catFilter === 'gia_pagate') return !!i.paid_in_xml;
      if (catFilter === 'all') return true;
      if (catFilter === 'iva') return i.vat_error;
      if (catFilter === 'excel_ko') return ['amount_diff', 'structure_mismatch', 'missing_in_excel'].includes(i.excel_status);
      return i.category === catFilter;
    })
    .filter(i => {
      if (!search) return true;
      const hay = norm([i.invoice_number, i.hotel_id, i.property_code, i.property_name, CAT[i.category]?.label, i.date, i.complaint_reason, i.complaint_booking, i.anagrafica_country].join(' '));
      return norm(search).split(/\s+/).filter(Boolean).every(t => hay.includes(t));
    })
    .sort((a, b) => { const va = ACC[sort.col](a), vb = ACC[sort.col](b); return (va < vb ? -1 : va > vb ? 1 : 0) * sort.dir; });
  const shownTotal = invoices.reduce((a, i) => a + (i.total || 0), 0);

  const Th = ({ label, col, align = 'left' }) => {
    const active = sort.col === col;
    const Icon = !active ? ArrowUpDown : sort.dir === 1 ? ArrowUp : ArrowDown;
    return (
      <th className={`p-2 text-${align} select-none cursor-pointer whitespace-nowrap`} data-testid={`bi-sort-${col}`}
        onClick={() => setSort(x => x.col === col ? { col, dir: -x.dir } : { col, dir: 1 })}>
        <span className={`inline-flex items-center gap-1 ${active ? 'text-slate-900 font-semibold' : ''}`}>{label} <Icon className={`h-3 w-3 ${active ? 'text-blue-600' : 'text-slate-300'}`} /></span>
      </th>
    );
  };

  const chips = [
    ['all', `Tutte${s ? ` (${s.invoice_count})` : ''}`],
    ['da_pagare', `Da pagare 💶${report ? ` (${payables.length})` : ''}`],
    ['gia_pagate', `Già in XML 💸${report ? ` (${all.filter(i => i.paid_in_xml).length})` : ''}`],
    ['prenotazioni', `Prenotazioni${s?.categories?.prenotazioni ? ` (${s.categories.prenotazioni.count})` : ''}`],
    ['reclamo_rimborso', `Reclami/Rimborsi${s?.categories?.reclamo_rimborso ? ` (${s.categories.reclamo_rimborso.count})` : ''}`],
    ['altro', `Altri costi${s?.categories?.altro ? ` (${s.categories.altro.count})` : ''}`],
    ['iva', `Con IVA ⚠️${s ? ` (${s.vat_error_count})` : ''}`],
    ['excel_ko', `Excel KO${s ? ` (${s.excel_diff + s.excel_missing})` : ''}`],
    ['chiusure', `Chiusure/Rinviate${s ? ` (${s.closure_count})` : ''}`],
  ];

  return (
    <div className="space-y-5" data-testid="booking-invoices-tab">
      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <h3 className="font-semibold text-slate-900">Fatture Booking.com — caricamento mensile</h3>
            <p className="text-xs text-slate-500">Carica lo ZIP dell'extranet (o PDF singoli) + l'Excel commissioni per il confronto. Classifico prenotazioni / reclami / altri costi, segnalo l'IVA (per noi è sempre un errore) e le case chiuse a lungo.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <label className="flex flex-col items-center justify-center p-5 border-2 border-dashed rounded-xl cursor-pointer hover:bg-slate-50">
              <FileArchive className="h-8 w-8 text-slate-400 mb-1" />
              <p className="text-sm font-medium text-slate-700">{files.length ? files.map(f => f.name).join(', ') : 'ZIP fatture (o più PDF)'}</p>
              <p className="text-xs text-slate-400">alldocuments_YYYY-MM.zip</p>
              <input type="file" className="hidden" multiple accept=".zip,.pdf" onChange={e => setFiles(Array.from(e.target.files || []))} data-testid="bi-files-input" />
            </label>
            <label className="flex flex-col items-center justify-center p-5 border-2 border-dashed rounded-xl cursor-pointer hover:bg-slate-50">
              <FileSpreadsheet className="h-8 w-8 text-emerald-500 mb-1" />
              <p className="text-sm font-medium text-slate-700">{excelFile ? excelFile.name : 'Excel commissioni (opzionale)'}</p>
              <p className="text-xs text-slate-400">per il confronto pre-pagamento</p>
              <input type="file" className="hidden" accept=".xlsx,.xls,.csv" onChange={e => setExcelFile(e.target.files?.[0] || null)} data-testid="bi-excel-input" />
            </label>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={analyze} disabled={loading || !files.length} data-testid="bi-analyze-btn">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analisi in corso…</> : <><Upload className="h-4 w-4 mr-2" /> Analizza fatture</>}
            </Button>
            <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none" title="File già pagati: solo verifica IVA/anomalie, nessun impatto su pagamenti, rinvii o XML">
              <input type="checkbox" checked={historical} onChange={e => setHistorical(e.target.checked)} data-testid="bi-historical-check" />
              Verifica storica (già pagato)
            </label>
            {history.length > 0 && (
              <select className="border rounded-md h-9 px-2 text-sm ml-auto" defaultValue="" onChange={e => e.target.value && openReport(e.target.value)} data-testid="bi-history-select">
                <option value="">📂 Storico report…</option>
                {history.map(h => <option key={h.id} value={h.id}>{(h.created_at || '').slice(0, 10)} · {h.period_from?.slice(0, 7) || '?'} · {h.summary?.invoice_count} fatture{h.historical ? ' · storico' : ''}</option>)}
              </select>
            )}
          </div>
        </CardContent>
      </Card>

      {alerts.length > 0 && (
        <Card className="border-rose-400 bg-rose-50" data-testid="bi-alerts">
          <CardContent className="p-4">
            <p className="text-sm font-bold text-rose-800 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> 🏠 Case RIAPERTE prima del previsto — fatture da rimettere in pagamento ({alerts.length})</p>
            <div className="mt-2 space-y-1">
              {alerts.map(a => (
                <div key={a.id} className="flex items-center gap-2 text-xs p-1.5 rounded bg-white border border-rose-200" data-testid={`bi-alert-${a.invoice_number}`}>
                  <b className="font-mono">{a.invoice_number}</b> · {a.property_name || a.property_code || a.hotel_id} · {eur(a.amount)} — chiusura prevista fino al <b>{a.old_closed_until}</b>{a.new_closed_until !== 'riaperta' ? <> , ora fino al <b>{a.new_closed_until}</b></> : ', ora RIAPERTA'}
                  <span className="ml-auto flex gap-1">
                    <button className="px-2 py-0.5 border rounded bg-emerald-600 text-white hover:bg-emerald-700 font-semibold" data-testid={`bi-alert-pay-${a.invoice_number}`} onClick={() => alertAction(a, 'pay')}>➜ Metti in pagamento</button>
                    <button className="px-2 py-0.5 border rounded bg-white text-slate-500 hover:bg-slate-50" data-testid={`bi-alert-ack-${a.invoice_number}`} onClick={() => alertAction(a, 'ack')}>Ho visto, lascia rinviata</button>
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {reminders?.deferrals?.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/50" data-testid="bi-reminders">
          <CardContent className="p-4">
            <p className="text-sm font-semibold text-amber-800 flex items-center gap-2"><BellRing className="h-4 w-4" /> Promemoria fatture rinviate ({reminders.deferrals.length})</p>
            <div className="mt-2 space-y-1">
              {reminders.deferrals.map(d => (
                <div key={d.id} className={`flex items-center gap-2 text-xs p-1.5 rounded ${highlightInv === d.invoice_number ? 'ring-2 ring-amber-500 ' : ''}${d.overdue ? 'bg-rose-100 text-rose-800' : d.due_soon ? 'bg-amber-100 text-amber-800' : 'text-slate-600'}`} data-testid={`bi-reminder-${d.invoice_number}`}>
                  <b className="font-mono">{d.invoice_number}</b> · {d.property_name || d.hotel_id} · {eur(d.amount)} — pagare entro <b>{d.pay_by}</b>
                  {d.overdue && <span className="font-bold">SCADUTO!</span>}{!d.overdue && d.due_soon && <span className="font-bold">in scadenza</span>}
                  <span className="text-slate-400">{d.note}</span>
                  <span className="ml-auto flex gap-1">
                    <button className="px-1.5 py-0.5 border rounded bg-white text-emerald-700 hover:bg-emerald-50" data-testid={`bi-paid-${d.invoice_number}`} onClick={() => deferAction(d, 'paid')}>✓ Pagata</button>
                    <button className="px-1.5 py-0.5 border rounded bg-white text-slate-500 hover:bg-slate-50" data-testid={`bi-cancel-${d.invoice_number}`} onClick={() => deferAction(d, 'cancel')}>↩ Annulla rinvio</button>
                  </span>
                </div>
              ))}
            </div>
            <p className="text-[10px] text-amber-700 mt-1.5">Le fatture rinviate vengono ESCLUSE automaticamente da preview, XML e split delle Commissioni Booking.com finché non le segni pagate o annulli il rinvio.</p>
          </CardContent>
        </Card>
      )}

      {vatReg?.holds?.length > 0 && (
        <Card className="border-rose-200" data-testid="bi-vat-register">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => setVatOpen(o => !o)} data-testid="bi-vat-register-toggle">
              <p className="text-sm font-semibold text-rose-800">💶 Registro IVA da recuperare da Booking ({vatReg.holds.length})</p>
              <span className="text-xs text-slate-500">
                {vatReg.totals?.blocked ? <>bloccate dagli XML: <b className="text-rose-700">{vatReg.totals.blocked.count} ({eur(vatReg.totals.blocked.vat_total)} IVA)</b> · </> : null}
                {vatReg.totals?.to_recover ? <>pagate, IVA da recuperare: <b className="text-amber-700">{vatReg.totals.to_recover.count} ({eur(vatReg.totals.to_recover.vat_total)})</b> · </> : null}
                {vatReg.totals?.recovered ? <>recuperate: <b className="text-emerald-700">{vatReg.totals.recovered.count} ({eur(vatReg.totals.recovered.vat_total)})</b></> : null}
              </span>
              <span className="ml-auto text-xs text-blue-600">{vatOpen ? 'nascondi ▲' : 'mostra ▼'}</span>
            </div>
            {vatOpen && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="text-slate-500 border-b">
                    <th className="p-1.5 text-left">Fattura</th><th className="p-1.5 text-left">Struttura</th><th className="p-1.5 text-left">Periodo</th>
                    <th className="p-1.5 text-right">Importo</th><th className="p-1.5 text-right">IVA</th><th className="p-1.5 text-left">Stato</th><th className="p-1.5 text-left">Azioni</th>
                  </tr></thead>
                  <tbody className="divide-y">
                    {vatReg.holds.map(h => (
                      <tr key={h.id} data-testid={`bi-vat-${h.invoice_number}`}>
                        <td className="p-1.5 font-mono">{h.invoice_number}</td>
                        <td className="p-1.5">{h.property_name || h.property_code || h.hotel_id}{h.anagrafica_country === 'IT' && <span className="text-rose-500 font-bold"> · anagrafica "Italy"</span>}</td>
                        <td className="p-1.5 whitespace-nowrap">{h.period_from ? `${h.period_from} → ${h.period_to}` : h.date}</td>
                        <td className="p-1.5 text-right">{eur(h.total)}</td>
                        <td className="p-1.5 text-right font-bold text-rose-600">{eur(h.vat_amount)}</td>
                        <td className="p-1.5">
                          {h.status === 'blocked' ? <span className="px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 font-bold">🚫 esclusa da XML</span>
                            : h.status === 'to_recover' ? <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">pagata · IVA da recuperare</span>
                            : <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold">✓ recuperata</span>}
                        </td>
                        <td className="p-1.5">
                          <span className="flex gap-1">
                            {h.status === 'blocked' && <button className="px-1.5 py-0.5 border rounded text-slate-600 hover:bg-slate-50" title="Rimettila negli XML e pagala; l'IVA resterà da recuperare" data-testid={`bi-vat-unblock-${h.invoice_number}`} onClick={() => vatAction(h, 'unblock')}>Sblocca e paga</button>}
                            {h.status !== 'recovered' && <button className="px-1.5 py-0.5 border rounded text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid={`bi-vat-recovered-${h.invoice_number}`} onClick={() => vatAction(h, 'recovered')}>✓ Recuperata</button>}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-rose-700 mt-1.5">Le fatture "bloccate" sono escluse automaticamente da preview, XML e split delle Commissioni finché non le sblocchi. Le storiche già pagate entrano come "IVA da recuperare".</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            {[['Fatture', s.invoice_count, '', eur(s.total_amount), false],
              ['Prenotazioni', s.categories?.prenotazioni?.count || 0, 'text-blue-600', eur(s.categories?.prenotazioni?.total || 0), false],
              ['Reclami/Rimborsi', s.categories?.reclamo_rimborso?.count || 0, 'text-purple-600', eur(s.categories?.reclamo_rimborso?.total || 0), false],
              ['Altri costi', (s.categories?.altro?.count || 0) + (s.categories?.nota_credito?.count || 0), 'text-slate-600', eur((s.categories?.altro?.total || 0) + (s.categories?.nota_credito?.total || 0)), false],
              ['IVA da contestare', s.vat_error_count, 'text-rose-600', eur(s.vat_error_total), false],
              ['Chiuse a lungo', s.closure_count, 'text-amber-600', '', false],
              ['Da pagare', payables.length, 'text-emerald-600', eur(payableTotal), true],
            ].map(([l, v, cls, sub, hl]) => (
              <Card key={l} className={hl ? 'border-emerald-400 bg-emerald-50/60' : ''}><CardContent className="p-3 text-center">
                <p className={`text-2xl font-bold ${cls}`} data-testid={`bi-stat-${l}`}>{v}</p>
                <p className="text-xs text-slate-500">{l}</p>
                {sub && <p className={`text-[11px] font-medium ${cls}`}>{sub}</p>}
              </CardContent></Card>
            ))}
          </div>

          {report.historical ? (
            <div className="p-3 rounded-lg bg-slate-100 border text-sm text-slate-500" data-testid="bi-payable-bar-historical">
              📜 Report storico (già pagato): la generazione XML non è disponibile.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-emerald-50 border border-emerald-300" data-testid="bi-payable-bar">
                <div className="text-sm text-emerald-900">
                  <b>Da pagare: {payables.length} fatture · {eur(payableTotal)}</b>
                  <span className="block text-xs text-emerald-700">
                    escluse automaticamente: {exclPaid.length > 0 && <b className="text-rose-700">{exclPaid.length} già in XML precedenti ({eur(sumOf(exclPaid))}) · </b>}{exclVat.length} bloccate per IVA ({eur(sumOf(exclVat))}) · {exclDeferred.length} rinviate ({eur(sumOf(exclDeferred))}) · {exclCredit.length} note di credito ({eur(sumOf(exclCredit))})
                  </span>
                  {needsSplit && <span className="block text-xs font-semibold text-amber-700">⚠ Totale sopra la soglia istantanei ({eur(splitCfg.effective_limit_eur)}): usa lo Split SEPA qui sotto (stessi lotti/massimali delle Commissioni Booking)</span>}
                </div>
                <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
                  <div className="flex items-center gap-1" title="Genera 2 XML: uno fino alla disponibilità indicata, uno con la differenza (giorno lavorativo successivo)">
                    <Input type="number" min="1" step="0.01" placeholder="Disponibilità €" value={fragAmount} onChange={e => setFragAmount(e.target.value)} className="h-9 w-36 bg-white" data-testid="bi-fragment-amount" />
                    <Button variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-100" onClick={fragmentXml} disabled={fragLoading || !payables.length} data-testid="bi-fragment-btn">
                      {fragLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Scissors className="h-4 w-4 mr-1" />} Frammenta in 2
                    </Button>
                  </div>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={generateXml}
                    disabled={xmlLoading || !payables.length || needsSplit}
                    title={needsSplit ? 'Disabilitato: totale sopra la soglia, usa lo Split SEPA sotto o Frammenta' : ''}
                    data-testid="bi-generate-xml-btn">
                    {xmlLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Genero…</> : <><FileDown className="h-4 w-4 mr-2" /> Genera XML Banca di Asti</>}
                  </Button>
                </div>
              </div>
              {blockingBatches.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-300 text-xs text-amber-900 space-y-1.5" data-testid="bi-blocking-batches">
                  <p><b>💸 {exclPaid.length} fatture escluse perché già dentro una distinta XML.</b> Per rigenerarle (es. col nuovo split istantanei/ordinari) elimina prima la distinta che le contiene:</p>
                  {blockingBatches.map(b => (
                    <div key={b.batch_id} className="flex flex-wrap items-center gap-2">
                      <span className="font-mono">{b.filename || b.batch_id}</span>
                      <span>· {b.date} · {b.count} fatture · <b>{eur(b.sum)}</b></span>
                      {b.batch_status === 'generated' ? (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-xs border-red-300 text-red-600 hover:bg-red-50" onClick={() => deleteBatch(b)} data-testid={`bi-del-batch-${b.batch_id}`}>Elimina distinta e sblocca</Button>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[10px]">eseguita in banca — non eliminabile da qui</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {needsSplit && (
                <SepaSplitPanel
                  jsonBody={{ report_id: report.id }}
                  planEndpoint="/api/booking-invoices/split-plan"
                  downloadEndpoint="/api/booking-invoices/split-download"
                  filenamePrefix="commissioni_booking"
                  title="Split SEPA — Limite Banca di Asti (Fatture Booking.com)"
                  onDownloaded={() => { toast.success('Lotti archiviati: le fatture risulteranno "già in XML"'); openReport(report.id); loadSplitCfg(); }}
                />
              )}
            </>
          )}

          {s.vat_error_count > 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800" data-testid="bi-vat-alert">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div><b>{s.vat_error_count} fatture con IVA addebitata ({eur(s.vat_error_total)})</b> — le nostre fatture sono reverse-charge, l'IVA va contestata a Booking.
                {s.anagrafica_it_count > 0 && <> Causa probabile: <b>{s.anagrafica_it_count} strutture con anagrafica "Italy"</b> sull'extranet (da correggere in "Spain").</>}</div>
            </div>
          )}

          {s.excel_loaded && (
            <div className={`p-3 rounded-lg border text-sm ${s.excel_diff + s.excel_missing + s.excel_only_count > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`} data-testid="bi-excel-recap">
              <b>Confronto Excel:</b> {s.excel_ok} quadrate · {s.excel_diff} con differenze · {s.excel_missing} fatture senza riga Excel · {s.excel_only_count} righe Excel senza PDF
              {report.excel_only?.length > 0 && <span className="block text-xs mt-1">Solo in Excel: {report.excel_only.map(x => `${x.invoice} (${x.property_name || x.hotel_id}, ${eur(x.amount)})`).join(' · ')}</span>}
            </div>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <Input placeholder="Filtro intelligente: fattura, struttura, casa, tipo, motivo reclamo…" value={search} onChange={e => setSearch(e.target.value)} className="max-w-md h-9" data-testid="bi-search" />
            <span className="text-xs text-slate-500 whitespace-nowrap" data-testid="bi-filtered-total">
              {invoices.length} visibili · <b className="text-slate-800">{eur(shownTotal)}</b>
            </span>
            <div className="flex gap-1 flex-wrap ml-auto">
              {chips.map(([k, l]) => (
                <button key={k} data-testid={`bi-chip-${k}`} onClick={() => setCatFilter(k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${catFilter === k ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>{l}</button>
              ))}
            </div>
          </div>

          <Card>
            <CardContent className="p-3">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs text-slate-500 border-b">
                    <Th label="Tipo" col="cat" /><Th label="Fattura" col="invoice" /><Th label="Struttura" col="prop" />
                    <Th label="Data" col="date" /><Th label="Periodo" col="period" /><Th label="Importo" col="total" align="right" />
                    <Th label="IVA" col="vat" align="right" /><Th label="Excel" col="excel" /><Th label="Chiusura" col="closure" />
                  </tr></thead>
                  <tbody className="divide-y">
                    {invoices.map(i => (
                      <tr key={i.filename} className={i.vat_error ? 'bg-rose-50/60' : ['amount_diff', 'structure_mismatch', 'missing_in_excel'].includes(i.excel_status) ? 'bg-amber-50/60' : 'hover:bg-slate-50'} data-testid={`bi-row-${i.invoice_number}`}>
                        <td className="p-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${CAT[i.category]?.cls}`}>{CAT[i.category]?.label}</span></td>
                        <td className="p-2 font-mono text-xs">{i.invoice_number}{i.is_payment_request && <span className="text-slate-400"> (rich. pag.)</span>}
                          {i.paid_in_xml && <p className="text-[10px] font-bold text-rose-600 whitespace-nowrap" title={`${i.paid_in_xml.filename || 'XML'} · ${eur(i.paid_in_xml.amount)} · batch ${i.paid_in_xml.batch_status === 'executed' ? 'eseguito' : 'generato'}`} data-testid={`bi-paid-badge-${i.invoice_number}`}>💸 già in XML del {i.paid_in_xml.date}</p>}
                        </td>
                        <td className="p-2 text-xs">
                          <span className="font-mono text-slate-400">{i.hotel_id}</span>
                          {i.property_name && <p className="max-w-[180px] truncate" title={i.property_name}>{i.property_code} · {i.property_name}</p>}
                          {i.anagrafica_country === 'IT' && <span className="text-[10px] text-rose-500 font-bold">anagrafica "Italy" ⚠️</span>}
                          {i.category === 'reclamo_rimborso' && i.complaint_reason && <p className="text-[10px] text-purple-600 max-w-[200px] truncate" title={i.complaint_reason}>pren. {i.complaint_booking}: {i.complaint_reason}</p>}
                        </td>
                        <td className="p-2 text-xs whitespace-nowrap">{i.date}</td>
                        <td className="p-2 text-xs whitespace-nowrap">{i.period_from ? `${i.period_from} → ${i.period_to}` : '—'}</td>
                        <td className="p-2 text-right font-medium whitespace-nowrap">{eur(i.total)}</td>
                        <td className={`p-2 text-right text-xs whitespace-nowrap ${i.vat_error ? 'text-rose-600 font-bold' : 'text-slate-400'}`}>
                          {i.vat_amount ? `${eur(i.vat_amount)} ⚠️` : '—'}
                          {i.vat_hold_status === 'blocked' && <p className="text-[10px] font-bold text-rose-700">🚫 esclusa da XML</p>}
                        </td>
                        <td className="p-2 text-xs whitespace-nowrap">
                          {i.excel_status ? <span className={EXCEL[i.excel_status]?.cls}>{EXCEL[i.excel_status]?.label}{i.excel_amount != null && ` (Excel ${eur(i.excel_amount)})`}{i.excel_hotel_id && ` (Excel ID ${i.excel_hotel_id})`}</span> : '—'}
                        </td>
                        <td className="p-2 text-xs">
                          {i.deferred ? (
                            <span className="text-amber-700 font-semibold whitespace-nowrap">⏸ rinviata → {i.deferred.pay_by}</span>
                          ) : i.closure ? (
                            <div className="whitespace-nowrap">
                              <span className="text-amber-700 font-semibold">chiusa fino al {i.closure.closed_until}</span>
                              <button className="ml-1 px-1.5 py-0.5 border rounded text-[10px] text-amber-700 border-amber-300 hover:bg-amber-50" data-testid={`bi-defer-${i.invoice_number}`} onClick={() => defer(i)}>Rinvia pagamento</button>
                            </div>
                          ) : <button className="px-1.5 py-0.5 border rounded text-[10px] text-slate-400 hover:bg-slate-50" title="Escludi dagli XML e rinvia a una data" data-testid={`bi-defer-${i.invoice_number}`} onClick={() => defer(i)}>Rinvia</button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!invoices.length && <p className="text-sm text-slate-400 py-4 text-center">Nessuna fattura con questi filtri</p>}
              </div>
            </CardContent>
          </Card>

          {report.parse_errors?.length > 0 && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-700" data-testid="bi-parse-errors">
              <b>{report.parse_errors.length} PDF non leggibili:</b> {report.parse_errors.map(e => e.filename).join(', ')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
